import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoutes, deleteRoute, setRouteEdit, clearRouteEdit } from '../../../../redux/actions/masters/route/route/routeMasterAction';
import dayjs from 'dayjs';
import { showConfirm, showInfo } from '../../../../utills/sweetAlert';
import SearchIcon from '@mui/icons-material/Search';

const cardStyle = {
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
  minWidth: 0,
  marginBottom: 0,
  fontFamily: 'Poppins, sans-serif',
};
const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
};
const titleStyle = {
  fontWeight: 700,
  fontSize: 18,
  color: '#111',
  letterSpacing: 0.7,
};
const newBtnStyle = {
  background: 'linear-gradient(135deg, #00C853 0%, #43A047 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 26px',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  marginRight: 12,
  letterSpacing: 0.5,
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 200, 83, 0.3)',
};
const searchContainer = {
  position: 'relative',
  minWidth: 200,
  marginLeft: 8,
};
const searchInput = {
  width: 200,
  padding: '10px 36px 10px 38px',
  border: '1.5px solid #C8E6C9',
  borderRadius: 8,
  fontSize: 16,
  color: '#1B5E20',
  background: '#fff',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border 0.3s',
};
const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#00C853',
  fontSize: 20,
  pointerEvents: 'none',
};
const actionStyle = {
  cursor: 'pointer',
  marginRight: 10,
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: 4,
  color: '#757575',
  transition: 'all 0.3s ease',
};

const RouteTable = ({ onNew, departmentsMap }) => {
  const dispatch = useDispatch();
  const { list = [], loading, error } = useSelector((state) => state.routeMaster);
  const [search, setSearch] = useState('');
  const [hoveredAction, setHoveredAction] = useState({});

  useEffect(() => {
    dispatch(fetchRoutes());
  }, [dispatch]);

  const handleEdit = async (route) => {
    await showInfo('Edit Route', `Editing route: ${route.route_name}`);
    dispatch(setRouteEdit(route));
  };

  const handleDelete = async (id, name) => {
    const confirmed = await showConfirm('Trash Route', `Are you sure you want to trash route: ${name}?`, 'Trash', 'Cancel');
    if (confirmed) {
      dispatch(deleteRoute(id));
    }
  };

  const handleNew = () => {
    dispatch(clearRouteEdit());
    if (typeof onNew === 'function') onNew();
  };

  const filteredRoutes = list.filter(rt =>
    rt.route_name && rt.route_name.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      name: 'Route Code',
      selector: row => row.route_code,
      sortable: true,
    },
    {
      name: 'Route Name',
      selector: row => row.route_name,
      sortable: true,
      width: '200px',
    },
    // {
    //   name: 'Company',
    //   selector: row => row.company_code,
    //   sortable: true,
    //   width: '120px',
    // },
    {
      name: 'Departments Name',
      selector: row => row.department_codes,
      sortable: false,
      width: '200px',
      cell: row => (row.department_codes || '').split(',').map(code => departmentsMap[code]?.department_name || code).join(', '),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '110px',
      cell: row => (
        <span style={{ color: row.status === 'Active' ? '#00C853' : '#d32f2f', fontWeight: 600 }}>{row.status}</span>
      ),
    },
    // {
    //   name: 'Created At',
    //   selector: row => row.created_at,
    //   sortable: true,
    //   cell: row => row.created_at ? dayjs(row.created_at).format('DD-MM-YYYY hh:mm A') : '-',
    //   width: '170px',
    // },
    // {
    //   name: 'Updated At',
    //   selector: row => row.updated_at,
    //   sortable: true,
    //   cell: row => row.updated_at ? dayjs(row.updated_at).format('DD-MM-YYYY hh:mm A') : '-',
    //   width: '170px',
    // },
    {
      name: 'Actions',
      cell: row => (
        <>
          <button
            style={actionStyle}
            onClick={() => handleEdit(row)}
            title="Edit"
            onMouseEnter={() => setHoveredAction({ id: row.route_id, type: 'edit' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <EditIcon fontSize="small" />
            {hoveredAction.id === row.route_id && hoveredAction.type === 'edit' && (
              <span style={{ marginLeft: 4, color: '#1B5E20', fontWeight: 600 }}>Edit</span>
            )}
          </button>
          <button
            style={actionStyle}
            onClick={() => handleDelete(row.route_id, row.route_name)}
            title="Trash"
            onMouseEnter={() => setHoveredAction({ id: row.route_id, type: 'delete' })}
            onMouseLeave={() => setHoveredAction({})}
          >
            <DeleteOutlineIcon fontSize="small" />
            {hoveredAction.id === row.route_id && hoveredAction.type === 'delete' && (
              <span style={{ marginLeft: 4, color: '#d32f2f', fontWeight: 600 }}>Trash</span>
            )}
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '140px',
    },
  ];

  return (
    <div style={cardStyle}>
      <style>{`
        .route-table ::-webkit-scrollbar {
          height: 6px;
          background: #f2f2f2;
        }
        .route-table ::-webkit-scrollbar-thumb {
          background: #d3d3d3;
          border-radius: 4px;
        }
        .route-table {
          scrollbar-color: #d3d3d3 #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
      <div style={headerStyle}>
        <span style={titleStyle}>Routes</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button style={newBtnStyle} onClick={handleNew}>+ New</button>
          <div style={searchContainer}>
            <input
              style={searchInput}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <SearchIcon style={{ ...searchIconStyle, color: '#00C853' }} />
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={filteredRoutes}
        pagination
        highlightOnHover
        striped
        fixedHeader
        fixedHeaderScrollHeight="420px"
        progressPending={loading}
        noDataComponent={<div style={{ color: '#888', fontSize: 16 }}>No Routes found.</div>}
        customStyles={{
          headRow: { style: { background: '#E8F5E9', fontWeight: 700 } },
          rows: { style: { minHeight: 48 } },
        }}
      />
      {error && <div style={{ color: 'red', fontSize: 15, marginTop: 8 }}>{error}</div>}
    </div>
  );
};

export default RouteTable; 